// SaveIndicator.jsx

import React, { useEffect, useState } from 'react';

// Services
import { uploadService } from 'services/nodes/NodeUploadService';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './SaveIndicator.css';

// Icons
import Icon from 'svg/Icon';

const getUploadStatusLabel = (upload) => {
  if (upload?.status === 'completed') return 'Upload complete';
  if (upload?.status === 'error') return 'Upload failed';
  return 'Upload cancelled';
};

const getUploadErrorMessage = (upload) => {
  const errorMessage = String(upload?.error?.message || '').trim();
  if (!errorMessage) return 'The upload could not be completed.';
  return errorMessage;
};

/**
 * SaveIndicator
 * 
 * Displays active and recent upload/save operations.
 * 
 * Shows progress bars for active uploads/saves and completion status for recent ones.
 * 
 * @returns {ReactNode} SaveIndicator component.
 */
const SaveIndicator = () => {

  // ----- STATE ----- //

  const [uploadStatus, setUploadStatus] = useState(uploadService.getStatus());
  const [hoveredCancelId, setHoveredCancelId] = useState('');

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- EFFECTS ----- //

  useEffect(() => {
    const unsubscribeUploads = uploadService.subscribe(setUploadStatus);
    return () => {
      unsubscribeUploads();
    };
  }, []);

  // ----- CONSTANTS ----- //

  const { activeUploads, completedUploads } = uploadStatus;

  /**
   * Filters to recent completed uploads within the last 10 seconds.
   */
  const recentCutoff = Date.now() - (10 * 1000); // 10 seconds ago
  const recentFailedUploads = completedUploads
    .filter((upload) => upload.completedAt && upload.completedAt > recentCutoff && upload.status === 'error')
    .slice(-3); // Show last 3 recent failed
  const recentCompletedUploads = completedUploads
    .filter((upload) => upload.completedAt && upload.completedAt > recentCutoff && upload.status !== 'error')
    .slice(-3); // Show last 3 recent completed

  const hasVisibleActivity =
    activeUploads.length > 0 ||
    recentFailedUploads.length > 0 ||
    recentCompletedUploads.length > 0;

  // ----- RETURN EARLY ----- //

  if (!hasVisibleActivity) return null;

  // ----- RENDER ----- //

  return (

    <div
      className="save-indicator-container"
      style={{
        '--save-indicator-surface': theme.panel,
        '--save-indicator-surface-border': theme.backgroundHover,
        '--save-indicator-active-surface': theme.overlay,
        '--save-indicator-active-border': theme.backgroundHover,
        '--save-indicator-active-foreground': theme.foreground,
        '--save-indicator-completed-surface': theme.success,
        '--save-indicator-completed-border': theme.success,
        '--save-indicator-completed-foreground': theme.highlightForeground,
        '--save-indicator-error-surface': theme.warning,
        '--save-indicator-error-border': theme.warning,
        '--save-indicator-error-foreground': theme.foreground,
        '--save-indicator-progress-track': theme.backgroundHover,
        '--save-indicator-progress-track-border': theme.backgroundMuted,
        '--save-indicator-progress-fill': theme.foreground,
        '--save-indicator-progress-glow': theme.foregroundMuted,
      }}
    >

      {/* ACTIVE UPLOADS */}
      {activeUploads.map(upload => (
        <div key={upload.id} className="save-indicator-item active">

          {/* ICON */}
          <Icon
            iconKey="upload"
            width={16}
            height={16}
            color={theme.foreground}
          />

          {/* TEXT */}
          <span className="save-indicator-text">
            Uploading {upload.completedFiles || 0}/{upload.totalFiles || 0} items
          </span>

          {/* PROGRESS */}
          <div className="save-indicator-progress">

            {/* PROGRESS BAR */}
            <div
              className="save-indicator-progress-bar"
              style={{ width: `${upload.progress || 0}%` }}
            />

          </div>

          {/* CANCEL BUTTON */}
          <button
            className="save-indicator-cancel"
            style={{
              backgroundColor: hoveredCancelId === upload.id ? theme.backgroundHover : theme.backgroundMuted,
              borderColor: theme.foregroundMuted,
              color: theme.foreground,
              transform: hoveredCancelId === upload.id ? 'scale(1.1)' : 'scale(1)',
            }}
            title="Cancel upload"
            onClick={() => uploadService.cancelUpload(upload.id)}
            onMouseEnter={() => setHoveredCancelId(upload.id)}
            onMouseLeave={() => setHoveredCancelId('')}
          >
            {/* CLOSE ICON */}
            <Icon
              iconKey="close"
              width={16}
              height={16}
            />
          </button>

        </div>
      ))}

      {/* RECENT FAILED UPLOADS */}
      {recentFailedUploads.map(upload => (
        <div key={upload.id} className="save-indicator-item failed">

          {/* ICON */}
          <Icon
            iconKey="close"
            width={16}
            height={16}
            color={theme.foreground}
          />

          <div className="save-indicator-message">

            {/* LABEL */}
            <span className="save-indicator-text">
              {getUploadStatusLabel(upload)}
            </span>

            {/* ERROR */}
            <span className="save-indicator-subtext">
              {getUploadErrorMessage(upload)}
            </span>

          </div>

        </div>
      ))}

      {/* RECENT COMPLETED UPLOADS */}
      {recentCompletedUploads.map(upload => (
        <div key={upload.id} className="save-indicator-item completed">

          {/* ICON */}
          <Icon
            iconKey="check"
            width={16}
            height={16}
            color={theme.highlightForeground}
          />

          {/* LABEL */}
          <span className="save-indicator-text">
            {getUploadStatusLabel(upload)}
          </span>

        </div>
      ))}

    </div>

  );
};

export default SaveIndicator;